import { useState, useEffect } from "react";

// ─── State types ──────────────────────────────────────────────────────────────
export type LiveStatusState = "ai-speaking" | "user-turn" | "ai-thinking" | null;

const STATUS_CONFIG: Record<string, {
    borderColor: string;
    text: string;
    textColor: string;
    indicator: "wave" | "green-pulse" | "dots";
}> = {
    "ai-speaking": {
        borderColor: "border-blue-200",
        text: "ourteam AI is speaking. Please wait until it finishes before responding.",
        textColor: "text-blue-800",
        indicator: "wave",
    },
    "user-turn": {
        borderColor: "border-green-200",
        text: "Your turn. Speak clearly and keep it focused, ourteam AI is listening.",
        textColor: "text-gray-700",
        indicator: "green-pulse",
    },
    "ai-thinking": {
        borderColor: "border-blue-200",
        text: "ourteam AI is thinking...",
        textColor: "text-blue-800",
        indicator: "dots",
    },
};

// ─── Indicator: animated waveform (ai-speaking) ──────────────────────────────
function WaveIndicator() {
    const bars = [
        { delay: "0s", height: "h-1" },
        { delay: "0.12s", height: "h-2.5" },
        { delay: "0.24s", height: "h-3.5" },
        { delay: "0.12s", height: "h-2.5" },
        { delay: "0s", height: "h-1" },
    ];

    return (
        <div className="flex items-center gap-[2px] h-3.5 flex-shrink-0">
            {bars.map((bar, i) => (
                <div
                    key={i}
                    className={`w-[2.5px] ${bar.height} rounded-full bg-blue-600 animate-[iv-wave_0.9s_ease-in-out_infinite]`}
                    style={{ animationDelay: bar.delay }}
                />
            ))}
        </div>
    );
}

// ─── Indicator: pulsing dot (user-turn) ──────────────────────────────────────
function GreenPulseIndicator() {
    return (
        <div className="relative w-2 h-2 flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-green-500 opacity-35 animate-[iv-ping_1.4s_ease-out_infinite]" />
            <div className="relative w-2 h-2 rounded-full bg-green-500" />
        </div>
    );
}

// ─── Indicator: bouncing dots (ai-thinking) ──────────────────────────────────
function ThinkingIndicator() {
    return (
        <div className="flex items-center gap-[3px] flex-shrink-0">
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className="w-[5px] h-[5px] rounded-full bg-blue-600 animate-[iv-think_1.2s_ease-in-out_infinite]"
                    style={{ animationDelay: `${i * 0.2}s` }}
                />
            ))}
        </div>
    );
}

// ─── LiveStatusBar ────────────────────────────────────────────────────────────
interface LiveStatusBarProps {
    state: LiveStatusState;
    aiName?: string;
}

export default function LiveStatusBar({ state, aiName = "ourteam AI" }: LiveStatusBarProps) {
    const [visible, setVisible] = useState(false);

    // Brief delay before showing so it doesn't flash on rapid state changes
    useEffect(() => {
        setVisible(false);
        const t = setTimeout(() => setVisible(true), 300);
        return () => clearTimeout(t);
    }, [state]);

    if (!visible || !state || !STATUS_CONFIG[state]) return null;

    const { borderColor, text, textColor, indicator } = STATUS_CONFIG[state];
    const displayText = aiName !== "" ? text.replace(/ourteam AI/g, aiName) : text;

    return (
        <>
            <div
                key={state}
                className={`absolute top-12 sm:top-4 left-2 right-2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 bg-white border ${borderColor} rounded-xl sm:rounded-full px-3 sm:px-4 py-2 flex items-center gap-2.5 z-20 sm:whitespace-nowrap animate-[iv-slideDown_0.2s_ease]`}
            >
                {indicator === "wave" && <WaveIndicator />}
                {indicator === "green-pulse" && <GreenPulseIndicator />}
                {indicator === "dots" && <ThinkingIndicator />}
                <span className={`text-xs font-medium min-w-0 break-words leading-snug ${textColor}`}>
                    {displayText}
                </span>
            </div>

            <style>{`
                @keyframes iv-wave {
                    0%, 100% { transform: scaleY(0.4); opacity: 0.5; }
                    50%      { transform: scaleY(1);   opacity: 1;   }
                }
                @keyframes iv-ping {
                    0%   { transform: scale(1);   opacity: 0.35; }
                    70%  { transform: scale(2.4); opacity: 0;    }
                    100% { transform: scale(2.4); opacity: 0;    }
                }
                @keyframes iv-think {
                    0%, 60%, 100% { transform: translateY(0);    opacity: 0.4; }
                    30%           { transform: translateY(-4px); opacity: 1;   }
                }
                @keyframes iv-slideDown {
                    from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
                    to   { opacity: 1; transform: translateX(-50%) translateY(0);    }
                }
            `}</style>
        </>
    );
}
